import React, {useCallback, useEffect, useRef, useState} from 'react';
import '../App.css';
import {instance} from "../Auth";
import Message from "../Chat/Message";
import MessageInput from "../Chat/MessageInput";
import Header from "../Chat/Header";
import {setUserId} from "../Store/Store";
import {CircleLoader} from 'react-spinners';
import {useSocket} from "../Hooks/useSocket";
import {$userId, useAuth} from "../Hooks/useAuth";
import {setUserIdForSocket} from "../api/Chat";
import Typing from "../Chat/Typing";
import {Sheet} from 'react-modal-sheet';
import {Emoji} from "../Store/Emoji";
import formatMessageDate from "../Helpers/FormatMessageDate";

interface Message {
    id: number;
    sender_id: number;
    recipient_id: number;
    content: string;
    created_at: string;
    is_read: number;
}

interface MessagesInterface {
    [date: string]: Message[];
}

function Chat() {
    const {isAuth} = useAuth();

    const [messages, setMessages] = useState<MessagesInterface>({});
    const [isLoading, setIsLoading] = useState(true);
    const [myEmojiId, setMyEmojiId] = useState(0);
    const [userEmojiId, setUserEmojiId] = useState(0);
    const [emojiSheet, setEmojiSheet] = useState(false);

    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const initialScrollDoneRef = useRef(false);

    const {
        partner,
        sendSocketMessage,
        typing
    } = useSocket({
        sendMessageCallback: () => {
            getChatMessages();
        }
    });

    useEffect(() => {
        setUserIdForSocket();
    }, []);

    const getChatMessages = useCallback(() => {
        instance
            .get('/chat')
            .then((res) => {
                setMessages(res.data.messages || {});
                setUserId(res.data.user.id);
                setMyEmojiId(res.data.user.emoji_id);
                setUserEmojiId(res.data.dialog_user.emoji_id);
            })
            .catch((error) => {
                console.error('Ошибка загрузки сообщений:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        getChatMessages();
    }, [getChatMessages]);

    useEffect(() => {
        if (isLoading || initialScrollDoneRef.current) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const container = chatContainerRef.current;

                if (!container) {
                    return;
                }

                container.scrollTop = container.scrollHeight;
                initialScrollDoneRef.current = true;
            });
        });
    }, [isLoading, messages]);

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    const setCurrentUserEmoji = (id: number) => {
        instance
            .post('/set-emoji/' + id)
            .then(() => {
                setEmojiSheet(false);
                setMyEmojiId(id);

                sendSocketMessage(
                    JSON.stringify({
                        action: 'ECHO',
                        data: true,
                        userId: $userId.getState()
                    })
                );
            })
            .catch((error) => {
                console.error('Ошибка при установке эмодзи:', error);
            });
    };

    const mySmile = Emoji.find(item => item.id === myEmojiId);

    return (
        <div className="App">
            <div
                className="loader"
                style={{display: isLoading ? "flex" : "none"}}
            >
                <CircleLoader
                    color="#36d7b7"
                    loading={true}
                    size={60}
                />
            </div>

            <Sheet
                detent="content-height"
                isOpen={emojiSheet}
                onClose={() => setEmojiSheet(false)}
            >
                <Sheet.Container>
                    <Sheet.Header/>
                    <Sheet.Content>
                        <Sheet.Scroller>
                            <div
                                style={{
                                    color: "white",
                                    padding: "16px",
                                    overflowX: "scroll"
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginBottom: "30px"
                                    }}
                                >
                                    {mySmile && (
                                        <picture>
                                            <source
                                                srcSet={mySmile.source}
                                                type="image/webp"
                                            />
                                            <img
                                                src={mySmile.img}
                                                alt="😎"
                                                width="78"
                                                height="78"
                                            />
                                        </picture>
                                    )}
                                </div>

                                <div className="emoji-container">
                                    {Emoji.map(item => (
                                        <div
                                            key={item.id}
                                            className={`item ${myEmojiId === item.id ? 'current-emoji-status' : ''}`}
                                            onClick={() => setCurrentUserEmoji(item.id)}
                                        >
                                            <picture>
                                                <source
                                                    srcSet={item.source}
                                                    type="image/webp"
                                                />
                                                <img
                                                    src={item.img}
                                                    alt="😎"
                                                    width="32"
                                                    height="32"
                                                    loading="lazy"
                                                />
                                            </picture>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Sheet.Scroller>
                    </Sheet.Content>
                </Sheet.Container>
                <Sheet.Backdrop/>
            </Sheet>

            <Header
                online={partner}
                setEmojiSheet={setEmojiSheet}
                userEmojiId={userEmojiId}
            />

            <div
                ref={chatContainerRef}
                className="chat-container"
            >
                <div className="chat-content">
                    {Object.entries(messages).map(([date, dayMessages]) => {
                        if (!Array.isArray(dayMessages)) {
                            return null;
                        }

                        return (
                            <div
                                className="chat-day"
                                key={date}
                            >
                                <div className="date-separator glass-effect">
                                    {formatMessageDate(date)}
                                </div>

                                {dayMessages.map(message => (
                                    <Message
                                        key={message.id}
                                        {...message}
                                        is_read={Boolean(message.is_read)}
                                    />
                                ))}
                            </div>
                        );
                    })}

                    <div className="typing-container">
                        <Typing typing={typing}/>
                    </div>

                    {!isStandalone && (
                        <div className="chat-bottom-space"/>
                    )}
                </div>
            </div>

            <MessageInput/>
        </div>
    );
}

export default Chat;
